import { FormEvent, useState } from 'react'
import { motion } from 'framer-motion'

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://facebook.com/AngelTech',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
        <path d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.6 1.7-1.6h1.8V3.8c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3V10H7.5v3h2.8v8h3.2Z" />
      </svg>
    )
  },
  {
    label: 'WhatsApp',
    href: 'https://wa.me/message/RZRXOYTVXCL7J1',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
        <path d="M12 3.2a8.7 8.7 0 0 0-7.5 13.1L3.3 20.7l4.6-1.2A8.8 8.8 0 1 0 12 3.2Zm0 15.9a7.1 7.1 0 0 1-3.6-1l-.3-.2-2.7.7.7-2.6-.2-.3a7.1 7.1 0 1 1 6.1 3.4Zm3.9-5.3c-.2-.1-1.3-.6-1.5-.7-.2-.1-.4-.1-.5.1l-.7.9c-.1.1-.2.1-.4.1-.2-.1-.9-.3-1.8-1.1-.7-.6-1.1-1.3-1.2-1.5-.1-.2 0-.3.1-.4l.4-.4c.1-.1.1-.2.2-.4 0-.1 0-.3-.1-.4l-.7-1.6c-.2-.4-.4-.4-.5-.4h-.4c-.1 0-.4.1-.6.3-.2.2-.8.8-.8 1.9s.8 2.2.9 2.4c.1.1 1.5 2.3 3.7 3.2.5.2 1 .4 1.3.5.5.2 1 .1 1.4.1.4-.1 1.3-.5 1.4-1 .2-.5.2-1 .1-1.1-.1-.1-.2-.2-.4-.3Z" />
      </svg>
    )
  },
  {
    label: 'Email',
    href: 'mailto:angeltechina@gmail.com',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8">
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </svg>
    )
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@prostheticlimb?_r=1&_t=ZP-98eyN6oW6ak',
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-current">
        <path d="M15.5 3h3a5.2 5.2 0 0 0 2.5 2.3v3.1a8 8 0 0 1-2.5-.7v6.1a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6 0 .9.1v3.2a2.6 2.6 0 1 0 1.8 2.4V3Z" />
      </svg>
    )
  }
]

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    const form = event.currentTarget
    const formData = new FormData(form)

    try {
      const response = await fetch('https://formsubmit.co/ajax/angeltechina@gmail.com', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })

      if (!response.ok) throw new Error('Unable to send inquiry')
      form.reset()
      setSubmitStatus('success')
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="bg-slate-950 py-20 text-white">
      <div className="container mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-teal-300">
              Contact
            </p>
            <h2 className="mt-4 text-4xl font-semibold leading-tight md:text-5xl">
              Send your product inquiry.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-300">
              Tell us the product code, target market and expected quantity. The team can support
              product selection, catalog information and export logistics coordination.
            </p>
            <div className="mt-9 space-y-4 text-sm text-slate-300">
              <p>
                <span className="font-semibold text-white">Email:</span> angeltechina@gmail.com
              </p>
              <p>
                <span className="font-semibold text-white">Products:</span> Prosthetics,
                orthotics, rehabilitation components and workshop materials
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3" aria-label="Social links">
              {socialLinks.map((socialLink) => (
                <a
                  key={socialLink.label}
                  href={socialLink.href}
                  target={socialLink.href.startsWith('mailto:') ? undefined : '_blank'}
                  rel={socialLink.href.startsWith('mailto:') ? undefined : 'noreferrer'}
                  aria-label={socialLink.label}
                  title={socialLink.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 text-slate-300 transition-colors hover:border-teal-300 hover:bg-teal-300 hover:text-slate-950"
                >
                  {socialLink.icon}
                </a>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            viewport={{ once: true }}
            className="rounded-lg bg-white p-6 text-slate-950 shadow-2xl shadow-black/20 md:p-8"
          >
            <div className="grid gap-5 md:grid-cols-2">
              <label className="block text-sm font-semibold">
                Name
                <input
                  type="text"
                  name="name"
                  required
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 font-normal outline-none transition-colors focus:border-teal-700"
                  placeholder="Your name"
                />
              </label>
              <label className="block text-sm font-semibold">
                Email
                <input
                  type="email"
                  name="email"
                  required
                  className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 font-normal outline-none transition-colors focus:border-teal-700"
                  placeholder="name@company.com"
                />
              </label>
            </div>
            <label className="mt-5 block text-sm font-semibold">
              Interested products
              <input
                type="text"
                name="interested_products"
                required
                className="mt-2 w-full rounded-md border border-slate-300 px-4 py-3 font-normal outline-none transition-colors focus:border-teal-700"
                placeholder="Example: A3-HKJ, A1-CFLD, ASL"
              />
            </label>
            <label className="mt-5 block text-sm font-semibold">
              Message
              <textarea
                name="message"
                required
                rows={5}
                className="mt-2 w-full resize-none rounded-md border border-slate-300 px-4 py-3 font-normal outline-none transition-colors focus:border-teal-700"
                placeholder="Share quantity, destination market, certification or packaging needs."
              />
            </label>
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-6 w-full rounded-md bg-teal-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-950"
            >
              {isSubmitting ? 'Sending...' : 'Submit Inquiry'}
            </button>
            {submitStatus === 'success' && (
              <p className="mt-4 text-center text-sm text-teal-700" role="status">
                Thank you. Your inquiry has been sent successfully.
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="mt-4 text-center text-sm text-red-600" role="alert">
                We could not send your inquiry. Please email angeltechina@gmail.com directly.
              </p>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  )
}

export default Contact
